import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './SenderSwitcher.css';

function SenderSwitcher({ accountId, onSwitch, disabled = false }) {
  const [senders, setSenders] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const loadSenders = useCallback(async () => {
    if (senders.length > 0) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/linkedin/accounts`, { timeout: 15000 });
      if (res.data.success) {
        setSenders(res.data.senders || []);
      }
    } catch (err) {
      console.warn('[SenderSwitcher] Failed to load senders:', err.message);
    } finally {
      setLoading(false);
    }
  }, [senders.length]);

  // Always load once on mount so the label shows the current sender's name
  useEffect(() => {
    loadSenders();
  }, [loadSenders]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const current = senders.find(s => s.account_id === accountId);
  const currentLabel = current
    ? (current.name || current.email || current.account_id)
    : (accountId ? 'Loading...' : 'No sender');

  const handleToggle = () => {
    if (disabled) return;
    if (!open) loadSenders();
    setOpen(!open);
  };

  const handlePick = (id) => {
    setOpen(false);
    if (id !== accountId && onSwitch) {
      onSwitch(id);
    }
  };

  return (
    <div className="sender-switcher" ref={containerRef}>
      <button
        className="sender-switcher-trigger"
        onClick={handleToggle}
        title={disabled ? 'Sender is locked while creating a campaign' : 'Switch LinkedIn sender'}
        disabled={disabled}
      >
        <span className="sender-switcher-icon">in</span>
        <span className="sender-switcher-label">{currentLabel}</span>
        <span className="sender-switcher-caret">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="sender-switcher-dropdown">
          <div className="sender-switcher-header">Switch sender</div>
          {loading && <div className="sender-switcher-empty">Loading...</div>}
          {!loading && senders.length === 0 && (
            <div className="sender-switcher-empty">No senders connected</div>
          )}
          {!loading && senders.map(s => (
            <button
              key={s.account_id}
              className={s.account_id === accountId ? 'sender-switcher-item active' : 'sender-switcher-item'}
              onClick={() => handlePick(s.account_id)}
            >
              <div className="sender-switcher-item-avatar">
                {(s.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="sender-switcher-item-info">
                <div className="sender-switcher-item-name">{s.name}</div>
                <div className="sender-switcher-item-meta">
                  {s.email || s.public_identifier || s.account_id}
                  {s.active_campaigns > 0 && (
                    <span className="sender-switcher-item-badge">
                      {s.active_campaigns} campaign{s.active_campaigns > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              {s.account_id === accountId && <span className="sender-switcher-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SenderSwitcher;
